import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { GerinusService } from '../lib/gerinus.service';
import { GlobalService } from '../lib/global.service';
import { environment } from 'src/environments/environment';
import { CryptoService } from './crypto.service';
import { localStorageEnum } from 'src/app/Enums/localStorageEnum';

@Injectable()
export class LoginService extends GerinusService {
    constructor(
        public global: GlobalService,
        public http: HttpClient,
        public router: Router,
        public cryptoService: CryptoService
    ) {
        super(http);
    }

    public authentication(isGovBr, code?, username?, password?) {
        if (isGovBr) {
            let auth = btoa(environment.govBR.clientID + ':' + environment.govBR.secret);
            let headers = { Authorization: 'Basic ' + auth };
            let url = `${environment.govBR.url}/token`;

            this.http
                .post(url, null, {
                    headers,
                    params: {
                        grant_type: 'authorization_code',
                        code,
                        redirect_uri: environment.govBR.returnURL,
                        code_verifier: environment.govBR.secret,
                    },
                })
                .subscribe({
                    next: (response: any) => {
                        const data = this.decodeJWT(response.id_token);
                        const access_token = response.access_token;

                        // O id_token não traz telefone (e o e-mail nele pode estar desatualizado);
                        // o userinfo é a fonte confiável dos dois para a sincronização de cadastro.
                        this.http
                            .get(`${environment.govBR.url}/userinfo`, {
                                headers: { Authorization: `Bearer ${access_token}` },
                            })
                            .subscribe({
                                next: (userInfo: any) => {
                                    this.completeGovBrLogin(
                                        { ...data, email: userInfo?.email || data?.email, phone_number: userInfo?.phone_number },
                                        access_token
                                    );
                                },
                                error: () => {
                                    this.completeGovBrLogin(data, access_token);
                                },
                            });
                    },
                });
        } else {
            let data = {
                username: username,
                password: password,
            };
            this.global.startLoading('Validando Usuário...');
            this.http.post(this.apiURL() + '/api/login', data, this.headers()).subscribe((response: any) => {
                if (response.ok) {
                    let user = response.user;
                    localStorage.setItem('user', JSON.stringify(user));
                    this.redirectAfterLogin();
                } else {
                    this.global.stopLoading();
                    this.global.swal.fire({
                        title: 'Erro!',
                        text: response.message,
                        icon: response.message_type,
                    });
                }
            });
        }
    }

    private completeGovBrLogin(data: any, access_token: string) {
        this.http
            .post(this.apiURL() + '/api/login', data, this.headers())
            .subscribe((response: any) => {
                if (response.ok) {
                    let user = response.user;
                    localStorage.setItem('user', JSON.stringify(user));
                    this.getAccountLevel(data.sub, access_token);
                    this.redirectAfterLogin();
                } else {
                    this.global.stopLoading();
                    this.global.swal.fire({
                        title: 'Erro!',
                        text: response.message,
                        icon: response.message_type,
                    });
                }
            });
    }

    public loginAlternativo(cpf: string, senha: string) {
        this.global.startLoading('Validando Usuário...');
        this.http.post(this.apiURL() + '/api/login', { cpf, senha }, this.headers()).subscribe({
            next: (response: any) => {
                this.global.stopLoading();
                if (response.ok) {
                    const user = { ...response.user, loginAlternativo: true };
                    localStorage.setItem('user', JSON.stringify(user));
                    this.redirectAfterLogin();
                }
                else {
                    this.global.swal.fire({ title: 'Erro!', text: response.message || 'CPF ou senha inválidos', icon: 'error' });
                }
            },
            error: () => {
                this.global.stopLoading();
                this.global.swal.fire({ title: 'Erro!', text: 'CPF ou senha inválidos', icon: 'error' });
            },
        });
    }

    public static saveRedirectUrl(url: string) {
        const ignoredRoutes = ['/principal', '/login', '/inicio'];
        if (url && url !== '/' && !ignoredRoutes.some(route => url.startsWith(route))) {
            localStorage.setItem(localStorageEnum.REDIRECT_URL, url);
        }
    }

    private redirectAfterLogin() {
        const redirectUrl = localStorage.getItem(localStorageEnum.REDIRECT_URL);
        localStorage.removeItem(localStorageEnum.REDIRECT_URL);

        if (redirectUrl) {
            this.router.navigateByUrl(redirectUrl, { replaceUrl: true });
        } else {
            this.router.navigate(['/inicio'], { replaceUrl: true });
        }
    }

    private async getAccountLevel(cpf: string, token: string) {
        const body = {
            cpf,
            access_token: token
        };

        do {
            const connection = await new Promise((resolve) => {
                this.http.post(`${environment.apiURL}/api/app/account-level`, body, this.headers()).subscribe({
                    next: (response) => {
    
                        if (response) {
                            const cryptoResponse = this.cryptoService.encrypt(JSON.stringify(response));
                            localStorage.setItem(localStorageEnum.ACCOUNT_GOVBR, cryptoResponse);
                            resolve(true);
                        }

                        resolve(false);
                    },
                    error: () => {
                        resolve(false);
                    }
                })
            });

            if (connection) {
                break;
            }

        } while (true)
        
    }

    public logout() {
        this.clearSessionFromLocalStorage();
        this.router.navigate(['/principal']);
    }

    public clearSessionFromLocalStorage() {
        localStorage.removeItem('user');
        localStorage.removeItem(localStorageEnum.ACCOUNT_GOVBR);
        localStorage.removeItem('modal-change-group-displayed');
        localStorage.removeItem('user-data-solicitacao');
        localStorage.removeItem('user-data-requerente');
        localStorage.removeItem('user-data-procuracao');
        localStorage.removeItem('user-data-check-another-applicant');
        localStorage.removeItem('update-registration-form');
        localStorage.removeItem('internal-process-form');
        localStorage.removeItem('new-request-descricao');
        localStorage.removeItem('attachments-request-documentos');
        localStorage.removeItem('form-request-inscricao-municipal');
         localStorage.removeItem('form-request-alvara');
        localStorage.removeItem('form-request-semurb');
        localStorage.removeItem('form-request-iptu');
        localStorage.removeItem('form-request-itbi');
        localStorage.removeItem('form-request-sead');
        localStorage.removeItem('form-request-dynamic');
    }

    public decodeJWT(token) {
        const parts = token.split('.');

        if (parts.length !== 3) {
            console.error('Token JWT inválido');
            return null;
        }

        const payload = this.base64UrlDecode(parts[1]);

        try {
            return JSON.parse(payload);
        } catch (error) {
            console.error('Erro ao analisar o payload do token:', error.message);
            return null;
        }
    }

    public base64UrlDecode(base64Url) {
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        return atob(base64);
    }
}
